import { Injectable, OnDestroy } from '@angular/core';

import { BehaviorSubject, Observable, combineLatest, Subscription } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { JournodexApiService } from '../../../api/journodex/journodex.api.service';
import { LocalizedEnum } from '../../../api/journodex/model/localized-enum';
import { JobRoleModel } from '../models/job-role.model';
import { JournodexEnumTypes } from '../../../api/journodex/enum/journodex-enum-types.enum';
import { productFrequencyMap } from '../../../api/journodex/enum/product-frequency-types.enum';
import { LocalizedEnumApiModel } from '../../../api/journodex/model/localized-enum-api.model';
import { mediaProductTypeMap } from '../../../api/journodex/enum/product-types.enum';
import { MediaMarket } from '../models/media-market.model';
import { IncludeExcludeModel } from '../../../Common/Misc/models/include-exclude-model';
import { JournodexGeographyModel } from '../../../Common/Journodex/models/journodex-geography.model';
import { TargetingPreferencesService } from '../../../Common/TargetingShared/services/targeting-preferences.service';
import { MediaMarketTypeEnum, mediaMarketTypeToGeoIdsMap, mediaMarketTypeToDisplayStringMap } from '../../../api/journodex/enum/media-market-type.enum';
import { MediaMarketApi } from '../../../api/journodex/model/media-market.api.model';
import { TargetingSearch } from '../../Targeting/models/targeting-search.model';
import { TargetingMediaTypeEnum } from '../../../Discover/Targeting/enums/targeting-media-type.enum';

/**
 * Product Types to filter out of api result
 */
const productTypeApiFilter: string[] = [
    'MEDIA_COMPANY'
];


interface MediaMarketGroup {
    label: string;
    type: MediaMarketTypeEnum;
    items: MediaMarket[];
};


// Provides all the enums (also known as reference data) for targeting
@Injectable()
export class JournodexEnumsService implements OnDestroy {

    jobRoles: JobRoleModel[];
    mediaTypes: LocalizedEnum[];
    productTypes: LocalizedEnum[];
    compatibleProductTypes: string[];
    marketSectors: LocalizedEnum[];
    coverageFocuses: LocalizedEnum[];
    productFrequencies: LocalizedEnum[];
    targetAudiences: LocalizedEnum[];
    circulationTypes: LocalizedEnum[];
    mediaMarkets: MediaMarket[];

    private jobRoles$ = new BehaviorSubject<JobRoleModel[]>(null);
    private mediaTypes$ = new BehaviorSubject<LocalizedEnum[]>(null);
    private productTypes$ = new BehaviorSubject<LocalizedEnum[]>(null);
    private compatibleProductTypes$ = new BehaviorSubject<string[]>(null);
    private marketSectors$ = new BehaviorSubject<LocalizedEnum[]>(null);
    private coverageFocuses$ = new BehaviorSubject<LocalizedEnum[]>(null);
    private productFrequencies$ = new BehaviorSubject<LocalizedEnum[]>(null);
    private targetAudiences$ = new BehaviorSubject<LocalizedEnum[]>(null);
    private circulationTypes$ = new BehaviorSubject<LocalizedEnum[]>(null);
    private mediaMarkets$ = new BehaviorSubject<MediaMarket[]>(null);

    private geographyPreferences: IncludeExcludeModel<JournodexGeographyModel>;
    private productTypesLoaded$: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private subscriptions: Subscription[] = [];

    readonly internalCirculationTypes = ['SIMILARWEB_UNIQUE_VISITORS', 'UNIQUE_VISITORS_PARTNERS'];

    constructor(
        private journodexApiService: JournodexApiService,
        private targetingPreferencesService: TargetingPreferencesService,
    ) {
        this.subscriptions.push(
            this.journodexApiService.getEnum(JournodexEnumTypes.productType).subscribe(productTypes => {
                this.productTypes = this.processProductTypes(productTypes);
                this.productTypes$.next(this.productTypes);
                this.productTypesLoaded$.next(true);

                this.updateCompatibleProductTypes([]);
            }),
            this.journodexApiService.getEnum(JournodexEnumTypes.mediaType).subscribe(mediaTypes => {
                this.mediaTypes = this.processMediaTypes(mediaTypes);
                this.mediaTypes$.next(this.mediaTypes);
            }),
            this.journodexApiService.getEnum(JournodexEnumTypes.marketSector).subscribe(marketSectors => {
                this.marketSectors = marketSectors.map(x => new LocalizedEnum(x));
                this.marketSectors$.next(this.marketSectors);
            }),
            this.journodexApiService.getEnum(JournodexEnumTypes.coverageFocus).subscribe(coverageFocuses => {
                this.coverageFocuses = coverageFocuses.map(x => new LocalizedEnum(x));
                this.coverageFocuses$.next(this.coverageFocuses);
            }),
            this.journodexApiService.getEnum(JournodexEnumTypes.productFrequency).subscribe(productFrequencies => {
                this.productFrequencies = this.processProductFrequencies(productFrequencies);
                this.productFrequencies$.next(this.productFrequencies);
            }),
            this.journodexApiService.getEnum(JournodexEnumTypes.targetAudience).subscribe(targetAudiences => {
                this.targetAudiences = targetAudiences.map(x => new LocalizedEnum(x));
                this.targetAudiences$.next(this.targetAudiences);
            }),
            this.journodexApiService.getAllJobRoles().subscribe(jobRoles => {
                this.jobRoles = jobRoles.map(x => new JobRoleModel(x));
                this.jobRoles$.next(this.jobRoles);
            }),
            this.journodexApiService.getEnum(JournodexEnumTypes.circulationType).subscribe(circulationTypes => {
                // The endpoint returns some "internal" values that are used
                // by the Journodex research application.   We don't want
                // to show them to Targeting clients so mask them here.
                // In other endpoints, these would get appropriately converted
                // to UNIQUE_VISITORS based on some business logic in the backend.
                // A perhaps better approach would be a different enum endpoint
                // that only returns customer-facing values.
                this.circulationTypes = circulationTypes
                    .map(x => new LocalizedEnum(x))
                    .filter(x => !this.internalCirculationTypes.includes(x.enumValue));

                this.circulationTypes$.next(this.circulationTypes);
            }),
            combineLatest([
                this.targetingPreferencesService.accountGeoPreferences$,
                this.journodexApiService.getAllMediaMarkets()
            ]).pipe(
                filter(([prefs, mediaMarkets]) => !!prefs && !!mediaMarkets)
            ).subscribe(([prefs, mediaMarkets]) => {
                this.geographyPreferences = prefs;

                this.mediaMarkets = this.applyRestrictionsToMediaMarkets(mediaMarkets).map(x => new MediaMarket(x));
                this.mediaMarkets$.next(this.mediaMarkets);
            })
        );
    }

    getMediaTypes(): Observable<LocalizedEnum[]> {
        return this.mediaTypes$;
    }

    getProductTypes(): Observable<LocalizedEnum[]> {
        return this.productTypes$;
    }

    getCompatibleProductTypes(): Observable<string[]> {
        return this.compatibleProductTypes$;
    }

    getMarketSectors(): Observable<LocalizedEnum[]> {
        return this.marketSectors$;
    }

    getCoverageFocuses(): Observable<LocalizedEnum[]> {
        return this.coverageFocuses$;
    }

    getProductFrequencies(): Observable<LocalizedEnum[]> {
        return this.productFrequencies$;
    }

    getTargetAudiences(): Observable<LocalizedEnum[]> {
        return this.targetAudiences$;
    }

    getCirculationTypes(): Observable<LocalizedEnum[]> {
        return this.circulationTypes$;
    }

    getAllJobRoles = (): Observable<JobRoleModel[]> => {
        return this.jobRoles$.pipe(filter(x => x !== null));
    };

    getJobRolesByIds(ids: number[]): Observable<JobRoleModel[]> {
        return this.getAllJobRoles().pipe(
            map(jobRoles => {
                return jobRoles.filter(jobRole => ids.indexOf(jobRole.id) > -1);
            } ));
    }

    jobRoleAutocomplete(searchString: string): Observable<JobRoleModel[]> {
        return this.getAllJobRoles().pipe(
            map(jobRoles => {
                return jobRoles.filter(jobRole => jobRole.displayString.toLocaleLowerCase().indexOf(searchString.toLocaleLowerCase()) > -1);
            } ));
    }

    getAllMediaMarkets(): Observable<MediaMarket[]> {
        return this.mediaMarkets$.pipe(filter(x => !!x));
    }

    getMediaMarketsByIds(ids: number[]): Observable<MediaMarket[]> {
        return this.mediaMarkets$.pipe(
            filter(x => !!x),
            map(mediaMarkets => {
                return mediaMarkets.filter(mm => ids.indexOf(mm.id) > -1);
            })
        );
    }

    mediaMarketAutocomplete(searchString: string): Observable<MediaMarket[]> {
        return this.getAllMediaMarkets().pipe(
            map(mediaMarkets => {
                return mediaMarkets.filter(mm => mm.displayString.toLocaleLowerCase().indexOf(searchString.toLocaleLowerCase()) > -1);
            })
        );
    }

    mediaMarketGroup(searchString: string): Observable<MediaMarketGroup[]> {
        return this.getAllMediaMarkets().pipe(
            map(mediaMarkets => {
                const filteredMediaMarkets = mediaMarkets.filter(mm => {
                    return mm.displayString.toLocaleLowerCase().indexOf(searchString.toLocaleLowerCase()) > -1;
                });

                let groupedItems: MediaMarketGroup[] = [
                    {
                        label: mediaMarketTypeToDisplayStringMap.get(MediaMarketTypeEnum.US_MEDIA_MARKETS),
                        type: MediaMarketTypeEnum.US_MEDIA_MARKETS,
                        items: []
                    },
                    {
                        label: mediaMarketTypeToDisplayStringMap.get(MediaMarketTypeEnum.UK_MEDIA_MARKETS),
                        type: MediaMarketTypeEnum.UK_MEDIA_MARKETS,
                        items: []
                    },
                    {
                        label: mediaMarketTypeToDisplayStringMap.get(MediaMarketTypeEnum.CA_MEDIA_MARKETS),
                        type: MediaMarketTypeEnum.CA_MEDIA_MARKETS,
                        items: []
                    }
                ];

                filteredMediaMarkets.forEach(item => {
                    const group = groupedItems.find(x => x.type === item.type);
                    group.items.push(item);
                });

                groupedItems = groupedItems.filter(x => x.items.length > 0);

                return groupedItems;
            }),

        );
    }

    updateCompatibleProductTypes(mediaTypes: TargetingMediaTypeEnum[]) {
        this.productTypesLoaded$.subscribe(ready => {
            if (!ready) {
                return;
            }

            if (mediaTypes.length === 0) {
                this.compatibleProductTypes = this.productTypes.map(x => x.enumValue);
                this.compatibleProductTypes$.next(this.compatibleProductTypes);
            } else {
                let compatibleProductTypes = [];
                mediaTypes.forEach(mediaType => {
                    compatibleProductTypes = compatibleProductTypes.concat(mediaProductTypeMap.get(mediaType));
                });
                compatibleProductTypes = compatibleProductTypes.filter((productType, index, self) =>
                    index === self.findIndex(x => x === productType)
                );
                this.compatibleProductTypes = compatibleProductTypes;
                this.compatibleProductTypes$.next(this.compatibleProductTypes);
            }
        });
    }


    /**
     * Filters out values that don't exist.
     * For validating AI generated searches as sometimes the AI can return random values.
     * Currently only checks include values as thats all the AI returns.
     * @param search the AI generated search
     */
    filterOutInvalidReferenceData(search: TargetingSearch) {

        if (search.jobRoles.include.length > 0) {
            search.jobRoles.include = search.jobRoles.include.filter(x => !!this.jobRoles.find(role => role.id === x));
        }

        if (search.mediaTypes.include.length > 0) {
            search.mediaTypes.include = search.mediaTypes.include.filter(x => !!this.mediaTypes.find(type => type.enumValue === x));
        }

        if (search.productTypes.include.length > 0) {
            search.productTypes.include = search.productTypes.include.filter(x => !!this.productTypes.find(type => type.enumValue === x));
        }

        if (search.marketSectors.include.length > 0) {
            search.marketSectors.include = search.marketSectors.include.filter(x => !!this.marketSectors.find(sector => sector.enumValue === x));
        }

        if (search.coverageFocuses.include.length > 0) {
            search.coverageFocuses.include = search.coverageFocuses.include.filter(x => !!this.coverageFocuses.find(sector => sector.enumValue === x));
        }

        if (search.productFrequencies.include.length > 0) {
            search.productFrequencies.include = search.productFrequencies.include.filter(
                x => !!this.productFrequencies.find(frequency => frequency.enumValue === x)
            );
        }

        if (search.targetAudiences.include.length > 0) {
            search.targetAudiences.include = search.targetAudiences.include.filter(x => !!this.targetAudiences.find(audience => audience.enumValue === x));
        }

        if (search.mediaMarkets.include.length > 0) {
            search.mediaMarkets.include = search.mediaMarkets.include.filter(x => !!this.mediaMarkets.find(market => market.id === x));
        }
    }

    /**
     * Filters out any media markets with types matching ids in the exclude preferences
     */
    private applyRestrictionsToMediaMarkets(mediaMarkets: MediaMarketApi[]): MediaMarketApi[] {
        return mediaMarkets.filter(mediaMarket => {
            const geoTypeIds = mediaMarketTypeToGeoIdsMap.get(mediaMarket.type);
            return !this.geographyPreferences.exclude.some(excludeModel => geoTypeIds.includes(excludeModel.collectionId));
        });
    }

    private processProductTypes(productTypes: LocalizedEnumApiModel[]): LocalizedEnum[] {
        return productTypes
            .filter(type => !productTypeApiFilter.includes(type.enumValue))
            .map(filteredType => new LocalizedEnum(filteredType))
            .sort((a, b) => a.displayString.toLowerCase() < b.displayString.toLowerCase() ?
                -1 : a.displayString.toLowerCase() > b.displayString.toLowerCase() ? 1 : 0);
    }

    private processMediaTypes(mediaTypes: LocalizedEnumApiModel[]): LocalizedEnum[] {
        return mediaTypes
            .filter(x => Object.values(TargetingMediaTypeEnum).includes(x.enumValue as TargetingMediaTypeEnum))
            .map(x => {
                x.displayString = `Discover.targeting.filter.mediaType.${x.enumValue as TargetingMediaTypeEnum}`;

                return new LocalizedEnum(x);
            });
    }

    private processProductFrequencies(productFrequencies: LocalizedEnumApiModel[]) {
        return productFrequencies.map(x => new LocalizedEnum(x)).sort((a, b) =>
            productFrequencyMap.get(a.enumValue) - productFrequencyMap.get(b.enumValue));
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
